import { Box, Typography } from '@mui/material';
import type { ReactElement, ReactNode, Ref } from 'react';

import type { AlternativeRow } from './alternative';
import { VISUALLY_HIDDEN } from './visuallyHidden';

/**
 * Cadre commun des graphiques : nom, alternative textuelle, etat sans donnee.
 *
 * Le dessin (SVG et legende) est **masque aux technologies d'assistance** et
 * remplace par son equivalent textuel, lu a la suite du nom du graphique :
 *
 * 1. une phrase de synthese ;
 * 2. une table portant **toutes** les valeurs, visuellement masquee mais
 *    exposee (`<caption>`, en-tetes de colonne et de ligne).
 *
 * Un lecteur d'ecran n'a donc pas a deviner une courbe : il lit les memes
 * valeurs que celles qui sont dessinees. Sans donnee, le cadre n'affiche **ni
 * axe ni graphique vide** mais une phrase, lue telle quelle.
 *
 * Aucune animation : rien ne s'impose donc a qui demande un mouvement reduit
 * (`prefers-reduced-motion`), et il n'y a rien a desactiver.
 */

export interface AlternativeTable {
  /** Intitule de la premiere colonne : « Jour », « Heure »… */
  readonly categoryHeader: string;
  readonly columns: readonly string[];
  readonly rows: readonly AlternativeRow[];
}

export interface ChartFrameProps {
  /** Nom accessible du graphique : ce qu'il montre, pas ce qu'il est. */
  readonly label: string;
  readonly summary: string;
  readonly table: AlternativeTable;
  /** Phrase affichee **a la place** du dessin quand rien n'est a dessiner. */
  readonly empty?: string;
  /** Le dessin : decoratif, il est masque aux lecteurs d'ecran. */
  readonly children?: ReactNode;
  /** Taille de son contenu plutot que toute la largeur (mini-courbe dans une tuile). */
  readonly inline?: boolean;
  readonly ref?: Ref<HTMLElement>;
}

export const ChartFrame = ({ label, summary, table, empty, children, inline = false, ref }: ChartFrameProps): ReactElement => (
  <Box component="figure" ref={ref} aria-label={label} sx={{ position: 'relative', m: 0, minWidth: 0, ...(inline ? { flex: 'none' } : { width: '100%' }) }}>
    {empty === undefined ? (
      <>
        <Box aria-hidden sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {children}
        </Box>
        <Box component="p" sx={{ ...VISUALLY_HIDDEN }}>
          {summary}
        </Box>
        <Box component="table" sx={{ ...VISUALLY_HIDDEN }}>
          <caption>{label}</caption>
          <thead>
            <tr>
              <th scope="col">{table.categoryHeader}</th>
              {table.columns.map((column, rank) => (
                <th key={rank} scope="col">
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, index) => (
              // L'abscisse peut se repeter (deux « 12:00 » sur 48 h) : le rang departage.
              <tr key={`${String(index)}-${row.category}`}>
                <th scope="row">{row.category}</th>
                {row.cells.map((cell, cellIndex) => (
                  <td key={cellIndex}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </Box>
      </>
    ) : (
      <Typography component="p" sx={{ fontSize: 14, color: 'tokens.textMuted', py: 1 }}>
        {empty}
      </Typography>
    )}
  </Box>
);
